import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ALL_EVENT_LEVELS,
  ALL_EVENT_ORGANIZERS,
  ALL_EVENT_TYPES,
  DEFAULT_FILTERS,
  PAGE_SIZE,
} from "../data/convocationsAdmin.constants";

import {
  buildEventLevelOptions,
  buildEventOrganizerOptions,
  buildEventTypeOptions,
  filterAndSortEvents,
  getConvocationsStats,
} from "../utils/convocationsAdmin.utils";

import useDebouncedValue from "./useDebouncedValue";

export default function useConvocationsFilters(events) {
  const [searchText, setSearchText] = useState(
    DEFAULT_FILTERS.search,
  );

  const [selectedType, setSelectedType] = useState(
    DEFAULT_FILTERS.type,
  );

  const [
    selectedOrganizer,
    setSelectedOrganizer,
  ] = useState(DEFAULT_FILTERS.organizer);

  const [selectedLevel, setSelectedLevel] = useState(
    DEFAULT_FILTERS.level,
  );

  const [
    selectedDateFilter,
    setSelectedDateFilter,
  ] = useState(DEFAULT_FILTERS.date);

  const [
    onlyWithConvocations,
    setOnlyWithConvocations,
  ] = useState(
    DEFAULT_FILTERS.onlyWithConvocations,
  );

  const [page, setPage] = useState(1);

  const debouncedSearch = useDebouncedValue(
    searchText,
    300,
  );

  /*
   * Les options sont toujours construites depuis
   * l'ensemble des événements non filtrés.
   */
  const typeOptions = useMemo(
    () => buildEventTypeOptions(events),
    [events],
  );

  const organizerOptions = useMemo(
    () => buildEventOrganizerOptions(events),
    [events],
  );

  const levelOptions = useMemo(
    () => buildEventLevelOptions(events),
    [events],
  );

  useEffect(() => {
    if (
      selectedType !== ALL_EVENT_TYPES &&
      !typeOptions.includes(selectedType)
    ) {
      setSelectedType(ALL_EVENT_TYPES);
    }
  }, [selectedType, typeOptions]);

  useEffect(() => {
    if (
      selectedOrganizer !==
        ALL_EVENT_ORGANIZERS &&
      !organizerOptions.includes(selectedOrganizer)
    ) {
      setSelectedOrganizer(
        ALL_EVENT_ORGANIZERS,
      );
    }
  }, [selectedOrganizer, organizerOptions]);

  useEffect(() => {
    if (
      selectedLevel !== ALL_EVENT_LEVELS &&
      !levelOptions.includes(selectedLevel)
    ) {
      setSelectedLevel(ALL_EVENT_LEVELS);
    }
  }, [selectedLevel, levelOptions]);

  const filteredEvents = useMemo(
    () =>
      filterAndSortEvents(events, {
        search: debouncedSearch,
        type: selectedType,
        organizer: selectedOrganizer,
        level: selectedLevel,
        date: selectedDateFilter,
        onlyWithConvocations,
      }),
    [
      events,
      debouncedSearch,
      selectedType,
      selectedOrganizer,
      selectedLevel,
      selectedDateFilter,
      onlyWithConvocations,
    ],
  );

  const stats = useMemo(
    () => getConvocationsStats(events),
    [events],
  );

  const pageCount = Math.max(
    1,
    Math.ceil(filteredEvents.length / PAGE_SIZE),
  );

  const safePage = Math.min(page, pageCount);

  const pageItems = useMemo(() => {
    const firstIndex =
      (safePage - 1) * PAGE_SIZE;

    return filteredEvents.slice(
      firstIndex,
      firstIndex + PAGE_SIZE,
    );
  }, [filteredEvents, safePage]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    selectedType,
    selectedOrganizer,
    selectedLevel,
    selectedDateFilter,
    onlyWithConvocations,
  ]);

  const resetFilters = () => {
    setSearchText(DEFAULT_FILTERS.search);
    setSelectedType(DEFAULT_FILTERS.type);

    setSelectedOrganizer(
      DEFAULT_FILTERS.organizer,
    );

    setSelectedLevel(DEFAULT_FILTERS.level);

    setSelectedDateFilter(
      DEFAULT_FILTERS.date,
    );

    setOnlyWithConvocations(
      DEFAULT_FILTERS.onlyWithConvocations,
    );

    setPage(1);
  };

  return {
    searchText,
    setSearchText,
    debouncedSearch,

    selectedType,
    setSelectedType,
    typeOptions,

    selectedOrganizer,
    setSelectedOrganizer,
    organizerOptions,

    selectedLevel,
    setSelectedLevel,
    levelOptions,

    selectedDateFilter,
    setSelectedDateFilter,

    onlyWithConvocations,
    setOnlyWithConvocations,

    filteredEvents,
    pageItems,

    page: safePage,
    setPage,
    pageCount,

    stats,
    resetFilters,
  };
}